/**
 * Global Error Handler
 * Catches uncaught exceptions and unhandled promise rejections
 * Maps technical errors to user-friendly messages
 */

type ErrorListener = (error: AppError) => void;

export enum ErrorCategory {
  NETWORK = "NETWORK",
  AUTH = "AUTH",
  VALIDATION = "VALIDATION",
  PERMISSION = "PERMISSION",
  SERVER = "SERVER",
  UNKNOWN = "UNKNOWN",
}

export interface AppError {
  message: string;
  category: ErrorCategory;
  originalError?: any;
  timestamp: number;
  userFriendlyMessage: string;
}

class GlobalErrorHandler {
  private listeners = new Set<ErrorListener>();
  private errorLog: AppError[] = [];
  private readonly MAX_LOG_SIZE = 100;

  constructor() {
    this.setupHandlers();
  }

  /**
   * Setup global error handlers
   */
  private setupHandlers(): void {
    // Catch uncaught exceptions
    window.addEventListener("error", (event) => {
      console.error("[GlobalErrorHandler] Uncaught error:", event.error);
      this.handleError(event.error || new Error(event.message));
      // Prevent default browser error handling
      event.preventDefault();
    });

    // Catch unhandled promise rejections
    window.addEventListener("unhandledrejection", (event) => {
      console.error("[GlobalErrorHandler] Unhandled rejection:", event.reason);
      this.handleError(event.reason);
      // Prevent default browser error handling
      event.preventDefault();
    });

    console.log("[GlobalErrorHandler] Initialized");
  }

  /**
   * Manually handle an error
   */
  handleError(error: any): void {
    const appError = this.transformError(error);
    this.logError(appError);
    this.notifyListeners(appError);
  }

  /**
   * Transform raw error to AppError
   */
  private transformError(error: any): AppError {
    const category = this.categorizeError(error);
    const userFriendlyMessage = this.getUserFriendlyMessage(error, category);

    return {
      message: error?.message || String(error),
      category,
      originalError: error,
      timestamp: Date.now(),
      userFriendlyMessage,
    };
  }

  /**
   * Categorize error type
   */
  private categorizeError(error: any): ErrorCategory {
    const message = error?.message?.toLowerCase() || "";
    const code = error?.code?.toLowerCase() || "";

    // Network errors
    if (
      message.includes("network") ||
      message.includes("fetch") ||
      message.includes("timeout") ||
      code.includes("network")
    ) {
      return ErrorCategory.NETWORK;
    }

    // Auth errors
    if (
      error?.status === 401 ||
      message.includes("unauthorized") ||
      message.includes("authentication") ||
      message.includes("token") ||
      code.includes("auth")
    ) {
      return ErrorCategory.AUTH;
    }

    // Permission errors
    if (
      error?.status === 403 ||
      message.includes("forbidden") ||
      message.includes("permission")
    ) {
      return ErrorCategory.PERMISSION;
    }

    // Validation errors
    if (
      error?.status === 400 ||
      message.includes("validation") ||
      message.includes("invalid") ||
      code.includes("22") // Postgres validation error codes
    ) {
      return ErrorCategory.VALIDATION;
    }

    // Server errors
    if (error?.status >= 500 && error?.status < 600) {
      return ErrorCategory.SERVER;
    }

    return ErrorCategory.UNKNOWN;
  }

  /**
   * Get user-friendly error message
   */
  private getUserFriendlyMessage(error: any, category: ErrorCategory): string {
    // Check for custom user message
    if (error?.userMessage) {
      return error.userMessage;
    }

    // Category-based messages
    switch (category) {
      case ErrorCategory.NETWORK:
        return "Unable to connect to the server. Please check your internet connection and try again.";

      case ErrorCategory.AUTH:
        return "Your session has expired. Please sign in again.";

      case ErrorCategory.PERMISSION:
        return "You don't have permission to perform this action.";

      case ErrorCategory.VALIDATION:
        return (
          error?.message ||
          "The information provided is invalid. Please check and try again."
        );

      case ErrorCategory.SERVER:
        return "Our servers are experiencing issues. Please try again in a few moments.";

      case ErrorCategory.UNKNOWN:
      default:
        return "An unexpected error occurred. Please try again.";
    }
  }

  /**
   * Log error to internal log
   */
  private logError(appError: AppError): void {
    this.errorLog.unshift(appError);

    // Keep log size manageable
    if (this.errorLog.length > this.MAX_LOG_SIZE) {
      this.errorLog = this.errorLog.slice(0, this.MAX_LOG_SIZE);
    }
  }

  /**
   * Notify all listeners
   */
  private notifyListeners(appError: AppError): void {
    this.listeners.forEach((listener) => {
      try {
        listener(appError);
      } catch (error) {
        console.error("[GlobalErrorHandler] Error in listener:", error);
      }
    });
  }

  /**
   * Subscribe to errors
   */
  subscribe(listener: ErrorListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Get error log (for debugging)
   */
  getErrorLog(): AppError[] {
    return [...this.errorLog];
  }

  /**
   * Clear error log
   */
  clearErrorLog(): void {
    this.errorLog = [];
  }
}

// Export singleton instance
export const globalErrorHandler = new GlobalErrorHandler();
